<script setup lang="ts">
import { computed } from 'vue';
import EntityPortrait from './EntityPortrait.vue';
import { explorerEntityExpansion, explorerReferenceExpansion, setExplorerEntityExpanded, setExplorerReferenceExpanded } from '../lib/explorerExpansionState';

interface ReferenceBranch {
  key: string;
  label: string;
  items: Array<{ id: string; title: string; profileImageData: string | null; status: 'active' | 'archived' | 'deleted' }>;
}

const props = defineProps<{
  entity: { id: string; title: string; icon?: string | null; profileImageData?: string | null; status?: 'active' | 'archived' | 'deleted'; isStoryDraft?: boolean };
  iconSrc: string;
  subclassLabel?: string | null;
  sceneNarrativeLabel?: string | null;
  active?: boolean;
  selected?: boolean;
  selectedEntityIds?: string[];
  branches?: ReferenceBranch[];
}>();
const emit = defineEmits<{ openEntity: [entityId: string]; openReference: [entityId: string]; dragStart: [entityId: string]; toggleSelection: [entityId: string] }>();

const hasReferences = computed(() => (props.branches?.length ?? 0) > 0);
function startDrag(event: DragEvent) {
  const entityIds = props.selectedEntityIds?.includes(props.entity.id) ? props.selectedEntityIds : [props.entity.id];
  event.dataTransfer?.setData('application/x-chronis-entity', props.entity.id);
  event.dataTransfer?.setData('application/x-chronis-entity-ids', JSON.stringify(entityIds));
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove';
  emit('dragStart', props.entity.id);
}
function openOrSelect(event: MouseEvent) {
  if (event.ctrlKey || event.metaKey) {
    emit('toggleSelection', props.entity.id);
    return;
  }
  emit('openEntity', props.entity.id);
}
function entityIsOpen() { return explorerEntityExpansion[props.entity.id] ?? false; }
function toggleEntity() {
  setExplorerEntityExpanded(props.entity.id, !entityIsOpen());
}
function stateKey(branch: ReferenceBranch) { return `${props.entity.id}:${branch.key}`; }
function isOpen(branch: ReferenceBranch) { return explorerReferenceExpansion[stateKey(branch)] ?? false; }
function toggle(branch: ReferenceBranch) {
  setExplorerReferenceExpanded(stateKey(branch), !isOpen(branch));
}
</script>

<template>
  <div class="category-entity-row">
    <div class="category-entity-heading">
      <button type="button" class="category-entity" :class="{ active, selected, archived: entity.status === 'archived', deleted: entity.status === 'deleted' }" :title="`${entity.title} – mit Strg klicken, um mehrere auszuwählen; zum Ablegen ziehen`" draggable="true" @dragstart="startDrag" @click="openOrSelect">
      <img class="entity-template-icon" :src="iconSrc" /><span class="entity-explorer-avatar" :class="{ empty: !entity.profileImageData }" aria-hidden="true"><img v-if="entity.profileImageData" :src="entity.profileImageData" alt="" /></span><span v-if="entity.status === 'archived'" class="entity-status-icon" title="Archiviert" aria-label="Archiviert">▱</span><strong v-if="sceneNarrativeLabel && entity.status !== 'deleted'" class="entity-subclass-badge">[{{ sceneNarrativeLabel }}]</strong><strong v-if="subclassLabel && entity.status !== 'deleted'" class="entity-subclass-badge">[{{ subclassLabel }}]</strong><strong v-if="entity.isStoryDraft && entity.status !== 'deleted'" class="entity-subclass-badge">[Entwurf]</strong>{{ entity.status === 'deleted' ? 'Entität gelöscht' : entity.title }}
      </button>
      <button v-if="hasReferences" type="button" class="entity-reference-toggle" :aria-label="entityIsOpen() ? `${entity.title} einklappen` : `${entity.title} ausklappen`" @click="toggleEntity">{{ entityIsOpen() ? '▾' : '▸' }}</button>
    </div>
    <template v-if="entityIsOpen()">
      <section v-for="branch in branches ?? []" :key="branch.key" class="category-reference-branch">
      <button type="button" class="category-reference-heading" :aria-label="isOpen(branch) ? 'Referenzliste einklappen' : 'Referenzliste ausklappen'" @click="toggle(branch)">
        <span>{{ isOpen(branch) ? '▾' : '▸' }}</span>{{ branch.label }} <small>{{ branch.items.length }}</small>
      </button>
      <button v-if="isOpen(branch)" v-for="item in branch.items" :key="item.id" type="button" class="category-reference-item" @click="emit('openReference', item.id)"><EntityPortrait :entity="item" size="xs" /> <span>↗ {{ item.title }}</span></button>
      </section>
    </template>
  </div>
</template>

<style scoped>
.category-reference-item{display:flex;align-items:center;gap:7px;padding-left:7px}
.category-reference-item span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.entity-subclass-badge{margin-right:5px;color:var(--ui-action-text);font-size:.78em;font-weight:900;letter-spacing:.02em}
</style>
